import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, Circle, ClipboardEdit, Clock, Loader2, Settings2, UserX, X } from 'lucide-react';
import { adminApi } from '../../services/api';
import { annoncerErreur, EtatErreur, SqueletteListe, useRequete } from '../../etats';
import QuestionnaireCoach from './QuestionnaireCoach';

/* ═══════════════════════════════════════════════════════════════════════
   OÙ EN SONT LES RÉPONSES — un diagnostic ouvert, personne par personne

   La carte du diagnostic ne donnait que des compteurs (« 2 dirigeants,
   1 membre ») : le coach ne savait pas qui manquait, et clôturait avec des
   questionnaires à moitié remplis. Ici, chaque participant avec son état,
   le questionnaire du coach — ouvert quand tous ont terminé — et le
   réglage de qui répond à quel domaine.
   ═══════════════════════════════════════════════════════════════════════ */

const TYPE = { initial: 'initial', mi_parcours: 'à mi-parcours', final: 'final' };

/* « Dirigeants 25 % · Membres 25 % · Coach 50 % » : ce que pèse chaque
   regard dans le score, selon les groupes qui ont répondu (un seul regard
   compte pour 100 %). Le serveur le calcule (`interpretation.base_du_calcul`). */
const GROUPES = [['dirigeant', 'Dirigeants'], ['membre', 'Membres'], ['coach', 'Coach']];
export function libelleBase(base) {
  const parts = GROUPES.filter(([cle]) => base?.[cle] != null)
    .map(([cle, nom]) => `${nom} ${String(base[cle]).replace('.', ',')} %`);
  return parts.length ? parts.join(' · ') : '';
}

const STATUTS = {
  termine: { libelle: 'Terminé', Icone: CheckCircle2, teinte: 'text-[var(--accent-texte)]' },
  en_cours: { libelle: 'En cours', Icone: Clock, teinte: 'text-[var(--attention)]' },
  pas_commence: { libelle: 'Pas commencé', Icone: Circle, teinte: 'text-[var(--text-muted)]' },
  sans_compte: { libelle: 'Sans compte', Icone: UserX, teinte: 'text-[var(--text-muted)]' },
};

function DomainesParGroupe({ diagnostic, fermes, onClose, onChange }) {
  const [structure, setStructure] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [etat, setEtat] = useState({ dirigeant: new Set(fermes.dirigeant || []), membre: new Set(fermes.membre || []) });
  const [enCours, setEnCours] = useState('');

  useEffect(() => {
    adminApi.getStructure().then((d) => setStructure(Array.isArray(d) ? d : [])).catch(setErreur);
  }, []);

  const basculer = async (domaine, groupe) => {
    const ferme = !etat[groupe].has(domaine.id);
    const suivant = { dirigeant: new Set(etat.dirigeant), membre: new Set(etat.membre) };
    if (ferme) suivant[groupe].add(domaine.id); else suivant[groupe].delete(domaine.id);
    setEnCours(`${domaine.id}-${groupe}`);
    try {
      await adminApi.reglerDomaine(diagnostic.id, {
        domaine_id: domaine.id,
        dirigeants_repondent: !suivant.dirigeant.has(domaine.id),
        membres_repondent: !suivant.membre.has(domaine.id),
      });
      setEtat(suivant);
      onChange?.();
    } catch (e) {
      annoncerErreur(e.message || 'Le réglage n’a pas pu être enregistré.');
    } finally {
      setEnCours('');
    }
  };

  return createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4" role="dialog" aria-modal="true"
         aria-label="Qui répond à quel domaine" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-[var(--bg-card)] rounded-cadre w-full max-w-xl shadow-carte flex flex-col max-h-[90vh]">
        <div className="flex items-start justify-between gap-3 p-6 pb-3">
          <div>
            <h3 className="text-xl font-black text-[var(--text-bold)]">Qui répond à quel domaine</h3>
            <p className="text-xs text-[var(--text-muted)] mt-1 font-semibold">
              Décochez un groupe pour qu’il ne voie pas ce domaine dans son questionnaire. Par défaut, tout le monde répond à tout.
            </p>
          </div>
          <button type="button" onClick={onClose} className="act act-icone act-tertiaire" aria-label="Fermer"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-6">
          {erreur ? <EtatErreur erreur={erreur} compact /> : !structure ? <SqueletteListe lignes={4} /> : (
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="text-[12px] text-[var(--text-muted)]">
                  <th className="py-2 font-semibold">Domaine</th>
                  <th className="py-2 font-semibold text-center w-24">Dirigeants</th>
                  <th className="py-2 font-semibold text-center w-24">Membres</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border)]">
                {structure.map((dom) => (
                  <tr key={dom.id}>
                    <td className="py-2.5 font-semibold text-[var(--text-bold)]">{dom.nom}</td>
                    {['dirigeant', 'membre'].map((groupe) => (
                      <td key={groupe} className="py-2.5 text-center">
                        {enCours === `${dom.id}-${groupe}`
                          ? <Loader2 className="w-4 h-4 animate-spin inline" />
                          : <input type="checkbox" checked={!etat[groupe].has(dom.id)} onChange={() => basculer(dom, groupe)}
                                   aria-label={`${groupe === 'dirigeant' ? 'Les dirigeants' : 'Les membres'} répondent à ${dom.nom}`}
                                   className="w-4 h-4 accent-[var(--accent)]" />}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default function SuiviReponses({ diagnostic, peutAgir, onChange }) {
  const requete = useRequete(() => adminApi.getEtatReponses(diagnostic.id), [diagnostic.id]);
  const etat = requete.donnees;
  const [questionnaire, setQuestionnaire] = useState(false);
  const [reglage, setReglage] = useState(false);

  const { recharger } = requete;
  const apresReponse = useCallback(() => { recharger(); onChange?.(); }, [recharger, onChange]);

  return (
    <section className="bg-[var(--bg-card)] rounded-carte shadow-carte p-5 sm:p-6" aria-labelledby={`suivi-${diagnostic.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <p className="surtitre text-[var(--text-muted)]">Diagnostic {TYPE[diagnostic.type_diagnostic] || diagnostic.type_diagnostic} en cours</p>
          <h3 id={`suivi-${diagnostic.id}`} className="text-lg font-black text-[var(--text-bold)]">Où en sont les réponses</h3>
        </div>
        {peutAgir && etat && (
          <button type="button" onClick={() => setReglage(true)} className="act act-sm act-contour">
            <Settings2 className="w-4 h-4" /> Qui répond à quoi
          </button>
        )}
      </div>

      {requete.statut === 'chargement' ? <SqueletteListe lignes={3} /> : requete.statut === 'erreur' ? (
        <EtatErreur erreur={requete.erreur} onReessayer={requete.recharger} compact />
      ) : etat && (
        <>
          {etat.participants.length === 0 && etat.autres_comptes.length === 0 ? (
            <p className="text-sm text-[var(--text-muted)] mb-4">Personne de l’organisation n’a encore répondu, et aucun participant n’est enregistré.</p>
          ) : (
            <ul className="divide-y divide-[var(--border)] mb-4">
              {etat.participants.map((p) => {
                const s = STATUTS[p.statut] || STATUTS.pas_commence;
                return (
                  <li key={p.id} className="flex items-center gap-3 py-2.5 text-sm">
                    <s.Icone className={`w-4 h-4 shrink-0 ${s.teinte}`} />
                    <span className="font-semibold text-[var(--text-bold)] min-w-0 truncate">{p.nom}</span>
                    <span className="text-[11px] text-[var(--text-muted)]">{p.role === 'DIRIGEANT' ? 'Dirigeant' : 'Membre'}</span>
                    <span className={`ml-auto text-[12px] font-semibold ${s.teinte}`}>
                      {s.libelle}{p.reponses ? ` · ${p.reponses} réponse${p.reponses > 1 ? 's' : ''}` : ''}
                    </span>
                  </li>
                );
              })}
              {etat.autres_comptes.map((c) => (
                <li key={c.nom} className="flex items-center gap-3 py-2.5 text-sm">
                  <CheckCircle2 className="w-4 h-4 shrink-0 text-[var(--accent-texte)]" />
                  <span className="font-semibold text-[var(--text-bold)] min-w-0 truncate">{c.nom}</span>
                  <span className="text-[11px] text-[var(--text-muted)]">{c.role === 'dirigeant' ? 'Dirigeant' : 'Membre'} · compte de l’organisation</span>
                  <span className="ml-auto text-[12px] font-semibold text-[var(--text-muted)]">{c.reponses} réponse{c.reponses > 1 ? 's' : ''}</span>
                </li>
              ))}
            </ul>
          )}

          {/* Le regard du coach : de préférence en dernier — recommandé, pas
              imposé. Le coach clôture quand il le décide. */}
          <div className="rounded-pastille border border-[var(--border)] bg-[var(--bg-main)] p-4 flex items-center gap-3 flex-wrap">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-[var(--text-bold)]">
                Regard du coach {etat.reponses_coach ? `· ${etat.reponses_coach} réponse${etat.reponses_coach > 1 ? 's' : ''}` : ''}
              </p>
              <p className="text-[12px] text-[var(--text-muted)]">
                {!etat.coach_peut_repondre ? etat.raison
                  : etat.recommandation || 'Tous les participants ont terminé : à vous de répondre, puis de clôturer.'}
              </p>
            </div>
            {etat.je_reponds_comme_coach && (
              <button type="button" onClick={() => setQuestionnaire(true)} disabled={!etat.coach_peut_repondre} className="act">
                <ClipboardEdit className="w-4 h-4" />
                {etat.reponses_coach ? 'Reprendre mon questionnaire' : 'Répondre au diagnostic'}
              </button>
            )}
          </div>
          {libelleBase(etat.base_du_calcul) && (
            <p className="text-[12px] text-[var(--text-muted)] mt-3">
              <span className="font-semibold text-[var(--text-bold)]">Si vous clôturez maintenant :</span>{' '}
              {libelleBase(etat.base_du_calcul)}. Seules les réponses déjà données comptent.
            </p>
          )}
          {!etat.je_reponds_comme_coach && etat.pourquoi_pas_moi && (
            <p className="text-[12px] text-[var(--text-muted)] mt-2">{etat.pourquoi_pas_moi}</p>
          )}
        </>
      )}

      {questionnaire && etat && (
        <QuestionnaireCoach diagnostic={diagnostic} dejaRepondu={etat.mes_reponses}
                            onClose={() => setQuestionnaire(false)} onEnregistre={apresReponse} />
      )}
      {reglage && etat && (
        <DomainesParGroupe diagnostic={diagnostic} fermes={etat.domaines_fermes}
                           onClose={() => setReglage(false)} onChange={requete.recharger} />
      )}
    </section>
  );
}
